import React, { useState, useRef } from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { magneticSpring } from '../../utils/animations';
interface ButtonProps extends HTMLMotionProps<'button'> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'white';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  className?: string;
  icon?: React.ReactNode;
}
export function Button({
  variant = 'primary',
  size = 'md',
  children,
  className = '',
  icon,
  ...props
}: ButtonProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState({
    x: 0,
    y: 0
  });
  const handleMouseMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    const {
      clientX,
      clientY
    } = e;
    const {
      left,
      top,
      width,
      height
    } = ref.current?.getBoundingClientRect() || {
      left: 0,
      top: 0,
      width: 0,
      height: 0
    };
    const x = clientX - (left + width / 2);
    const y = clientY - (top + height / 2);
    setPosition({
      x,
      y
    });
  };
  const handleMouseLeave = () => {
    setPosition({
      x: 0,
      y: 0
    });
  };
  const baseStyles = 'relative inline-flex items-center justify-center rounded-full font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none overflow-hidden touch-manipulation';
  const variants = {
    primary: 'bg-slate-900 text-white hover:bg-slate-800 focus:ring-slate-900 shadow-lg shadow-slate-900/20',
    secondary: 'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-600 shadow-lg shadow-indigo-600/20',
    outline: 'border-2 border-slate-900 bg-transparent text-slate-900 hover:bg-slate-900 hover:text-white focus:ring-slate-900 transition-all',
    ghost: 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/50 focus:ring-slate-500',
    white: 'bg-white text-slate-900 hover:bg-slate-50 focus:ring-white shadow-lg shadow-slate-900/10 border border-slate-200'
  };
  const sizes = {
    sm: 'h-10 px-5 text-sm',
    md: 'h-12 px-6 text-base',
    lg: 'h-14 px-8 text-lg'
  };
  return <motion.button ref={ref} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} animate={{
    x: position.x * 0.15,
    y: position.y * 0.15
  }} transition={magneticSpring} whileHover={{
    scale: 1.03
  }} whileTap={{
    scale: 0.97
  }} className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`} {...props} data-id="element-368">
      {/* Glow effect for primary/secondary */}
      {(variant === 'primary' || variant === 'secondary') && <motion.div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-500" style={{
      background: 'radial-gradient(circle at center, rgba(255,255,255,0.2) 0%, transparent 70%)'
    }} data-id="element-369" />}

      {/* Button content */}
      <span className="relative z-10 flex items-center gap-2" data-id="element-370">
        {children}
        {icon && <motion.span className="opacity-80" animate={{
        x: position.x * 0.1,
        y: position.y * 0.1
      }} data-id="element-371">
            {icon}
          </motion.span>}
      </span>

      {/* Ripple effect on click */}
      <motion.span className="absolute inset-0 bg-white/20 rounded-full" initial={{
      scale: 0,
      opacity: 0
    }} whileTap={{
      scale: 2,
      opacity: [0, 0.3, 0]
    }} transition={{
      duration: 0.4
    }} data-id="element-372" />
    </motion.button>;
}